/**
 * This component is used to select tables in sql template forms of type FormType.ENTITY. It works by listing
 * a dropdown of tagSchemas and returning warehouseTableDatas + schemaName on selections. We show tagSchemas
 * as dropdowns to be able to use additional information in the dropdown like the tagSchemas' icon and entity
 * types, and schema names can be updated without updating the warehouse table name, which would look odd for
 * a non-warehouse aware user. We then return warehouseTableDatas to be easy to use with the rest of the sql
 * template form.
 */
import {objectFromEntries, naturalSortByKey} from '@benchling/js-utils';
import React, {useMemo} from 'react';

import {isRegistrySupportedType} from 'constants/RegistryConstants';
import {QueryResultType} from 'graphql-client/BenchlingQuery';
import useWarehouseModelsQuery from 'insights/warehouse/useWarehouseModelsQuery';
import {UserWarehouseModelsData, WarehouseTableData} from 'insights/warehouse/warehouseModelsDataLayout';
import {getSchemaSearchableMenuOptionDatas} from 'samples/SchemaStarsUtil';
import {getTagSchemaOligoAwareFolderItemType} from 'tag-schema/util/TagSchemaUtil';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import SearchableMenu, {SearchableMenuArchivableOptionDatas} from 'ui-library/menus/SearchableMenu';
import {capwords, typeToName} from 'util/NameUtil';

import {TagSchemaListingGraphQL} from '../../../../../graphql-fragments/schemas/__generated/TagSchemaListingGraphQL';

export function getBioentityTableSelectorOptions(
  tagSchemas: Array<TagSchemaListingGraphQL>
): SearchableMenuArchivableOptionDatas {
  const supportedTagSchemas = tagSchemas.filter((tagSchema) =>
    isRegistrySupportedType(tagSchema.folderItemType)
  );

  const tagSchemaTypes = [
    ...new Set(supportedTagSchemas.map((tagSchema) => getTagSchemaOligoAwareFolderItemType(tagSchema))),
  ];

  return getSchemaSearchableMenuOptionDatas(supportedTagSchemas, [], {
    sections: naturalSortByKey(
      tagSchemaTypes.map((type) => ({
        title: capwords(typeToName(type)),
        containsSchema: (tagSchema: TagSchemaListingGraphQL) =>
          getTagSchemaOligoAwareFolderItemType(tagSchema) === type,
      })),
      'title'
    ),
  }).map((menuOption) =>
    menuOption.className === 'schema-option'
      ? {
          ...menuOption,
          // getSchemaSearchableMenuOptionDatas doesn't support adding icons, so we have to manually add it
          // here
          icon: menuOption.value.iconId,
        }
      : menuOption
  );
}

export interface BioentityTableSelectorProps {
  setSelectedTable: (warehouseTable: WarehouseTableData | null) => void;
  selectedTable: WarehouseTableData | null;
  warehouseModelsData: UserWarehouseModelsData;
}

export function BioentityTableSelector(props: BioentityTableSelectorProps): JSX.Element {
  const [tagSchemaIdToTableLayout, menuOptions] = useMemo(() => {
    const tablesWithTagSchemas = [...props.warehouseModelsData.values()]
      // Extract all table datas
      .flatMap((modelData) => [...modelData.tables.values()])
      // Filter out tables that don't have corresponding tag schemas
      .filter((tableData) => tableData.schema?.__typename === 'TagSchemaNode');

    const tagSchemaIdToTableLayout = objectFromEntries(
      tablesWithTagSchemas.map((tableData) => [(tableData.schema as TagSchemaListingGraphQL).id, tableData])
    );

    const tagSchemas = tablesWithTagSchemas.map((tableData) => tableData.schema as TagSchemaListingGraphQL);
    const menuOptions = getBioentityTableSelectorOptions(tagSchemas);

    return [tagSchemaIdToTableLayout, menuOptions];
  }, [props.warehouseModelsData]);

  const selectedTagSchema =
    props.selectedTable == null ? null : (props.selectedTable.schema as TagSchemaListingGraphQL);

  function handleChangeSelectedTable(selectedTagSchema: TagSchemaListingGraphQL): void {
    const selectedTable = tagSchemaIdToTableLayout[selectedTagSchema.id];
    props.setSelectedTable(selectedTable);
  }

  return (
    <SelectDropdown
      displayName={selectedTagSchema?.name ?? 'Select an entity schema'}
      menu={
        <SearchableMenu
          onSelect={handleChangeSelectedTable}
          optionDatas={menuOptions}
          selectedValue={selectedTagSchema}
        />
      }
      dataTestAttrs={{component: 'BioentityTableSelector', element: 'root'}}
    />
  );
}

// TODO(BNCH-27800): Refactor component into QueryingWarehouseTableSelector
export default function QueryingBioentityTableSelector(
  props: Omit<BioentityTableSelectorProps, 'warehouseModelsData'>
): JSX.Element | null {
  const warehouseModelsResult = useWarehouseModelsQuery({sortColumns: true});

  if (warehouseModelsResult.queryResultType === QueryResultType.SUCCESS) {
    return <BioentityTableSelector {...props} warehouseModelsData={warehouseModelsResult.transformedData} />;
  } else if (warehouseModelsResult.queryResultType === QueryResultType.PENDING) {
    return <CenteredHorizontalThrobber />;
  } else {
    return null;
  }
}
